/* Copyright (c) 2018 Backpack Health LLC */

import React, { FC, useEffect } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import * as _ from 'lodash';

import { Card, Icons } from '../../../../bphComponents/bundle';

import ContentFrame from '../../../applicationFrame/ContentFrame';
import LogoBackpackRegistered from '../../../components/icons/LogoBackpackRegistered';
import { Button } from '../../../components/buttons/Buttons';
import { ExternalLink } from '../../../components/TextLink';
import LegalDocumentLinks from '../../../components/LegalDocumentLinks';
import { PreferredLanguageDropdownList } from '../../../components/LanguageDropdownList';
import { ScrollAwareContext, ScrollAwareScroller } from '../../../components/ScrollAware';
import NotFound from '../../NotFound';
import { GroupThemeMarkdown } from '../../../components/Markdown';
import GroupMetadata, { GroupMetadataType } from '../../../../models/GroupMetadata';
import IconPrivacy from '../../../components/icons/IconPrivacy';
import * as IconSecurity from '../../../components/icons/IconSecurity';

import COLORS from '../../../styles/Colors';
import I18nText, { FormattedMessageWithI18nValues, I18nImage, I18nImageCollection } from '../../../components/I18nTranslation';
import GroupBanner from '../../../appAuthorized/views/groups/GroupDetails/GroupBanner';
import GroupSunset from '../../../components/GroupSunset';

const LandingIcon = (props) => {
  const { Icon, phraseUrl, phraseText, className } = props;

  return (
    <div className="cmp-landingicon" style={{ display: 'flex', alignItems: 'center' }}>
      <Icon color={COLORS.black} />
      <FormattedMessage {...phraseUrl}>
        {
          (url: string) => (
            <ExternalLink href={url} className={className} style={{ marginLeft: 16 }}>
              <FormattedMessage {...phraseText} />
            </ExternalLink>
          )
        }
      </FormattedMessage>
    </div>
  );
};

interface GroupJoinInstructionsHeaderProps {
  group: GroupMetadata;
  intl: any;
}
const GroupJoinInstructionsHeader: FC<GroupJoinInstructionsHeaderProps> = (props) => {
  const { group, intl } = props;
  const hasBanner = group.imagesBanner.hasValue();
  return (
    <div className="cmp-groupjoininstructionsheader">
      {
        hasBanner
        ? <GroupBanner imageBanner={group.imagesBanner.get()} />
        : <div className="__header" style={{ background: `linear-gradient(180deg, ${group.theme.getBaseCssColor()} 0%, ${group.theme.getHighlightCssColor()} 100%)` }} />
      }

      {
        group.imagesIcon.hasValue(intl.locale)
        ? (
          <div className="__group-icon">
            <I18nImage data={group.imagesIcon.get()} />
          </div>
        ) : (
          <div className="__group-icon" style={{ backgroundColor: group.theme.getBaseCssColor() }}>
            {
              group.groupName.getInitials()
            }
          </div>
        )
      }
    </div>
  );
};

const IntlGroupJoinInstructionsHeader = injectIntl(GroupJoinInstructionsHeader);

interface GroupEligibiltyProps {
  intl: any;
  groupMetadata: GroupMetadataType;
  userData: any;
  goToJoinGroup(g, u, l): void;
  className?: string;
}
const GroupEligibility: FC<GroupEligibiltyProps> = (props) => {
  const { groupMetadata, userData, goToJoinGroup, className, intl }  = props;
  const group = new GroupMetadata(groupMetadata);
  const { discoverable } = groupMetadata;
  return (
    <div className={className}>
      <Card.CardShape>
        <Card.CardContent>
          <h3><FormattedMessage id="groupJoinEligibility.eligibility.heading.text" /></h3>
          <I18nText data={group.eligibility.get()}>
            {
              (text) => (
                text ? <div className="__custom-markdown"><GroupThemeMarkdown text={text} /></div> : <div className="font-subhead"><FormattedMessage id="groupJoinEligibility.noCriteria.message.text" /></div>
              )
            }
          </I18nText>

          <div>
            <Button
              action="backpack"
              onClick={() => goToJoinGroup(groupMetadata, userData, intl.locale)}
              size="wide"
              className="theme-group base background cmp-text-heading"
              isDisabled={!discoverable}
            >
              <FormattedMessage id={discoverable ? 'joinGroupInstructions.joinGroup.button.label' : 'joinGroupInstructions.joinGroup.button.disabled.label'} />
            </Button>
          </div>
        </Card.CardContent>
      </Card.CardShape>
    </div>
  );
};

const IntlGroupEligibility = injectIntl(GroupEligibility);

interface OrganizationDetailsProps {
  groupMetadata: GroupMetadataType;
  className?: string;
}
const OrganizationDetails: FC<OrganizationDetailsProps> = (props) => {
  const { groupMetadata, className }  = props;
  const group = new GroupMetadata(groupMetadata);

  const hasOrgLogo = group.imagesOrgLogo.hasValue();
  const hasOrgDesc = group.organizationDescription.hasValue();
  const hasOrgUrl = group.organizationUrl.hasValue();
  const hasContent = !!(hasOrgLogo || hasOrgDesc || hasOrgUrl);
  if (!hasContent) { return null; }

  return (
    <div className={className} style={{ marginTop: 32 }}>
      <I18nImage data={group.imagesOrgLogo.get()} style={{ maxHeight: 80, maxWidth: 240 }} />

      <I18nText
        data={group.organizationDescription.get()}
        className="font-subhead font-color-gsDarkest"
        style={{ marginTop: 24 }}
      />

      <I18nText data={group.organizationUrl.get()}>
        {
          (text) => (
            text
            ? (
              <div style={{ marginTop: 8 }}>
                <ExternalLink
                  href={text}
                  className="theme-group base color"
                  cutProtocols={true}
                >
                  {text}
                </ExternalLink>
              </div>
            ) : null
          )
        }
      </I18nText>
    </div>
  );
}

export interface GroupJoinInstructionsProps {
  groupMetadata: GroupMetadataType;
  subdomain: string;
  setActiveGroup(nextGroupId: number, currentGroupId: number): void;
  fetchGroup(subdomain: string): void;
  goToJoinGroup(groupData, userData, lang): void;
  goBack(): void;
  activeGroupId: number;
  isRequestFailed: boolean;
  userData: any;
}

const GroupJoinInstructions: FC<GroupJoinInstructionsProps> = (props) => {
  const { subdomain, fetchGroup, setActiveGroup, activeGroupId, groupMetadata, goToJoinGroup, userData, isRequestFailed, goBack } = props;
  const group = new GroupMetadata(groupMetadata);
  const groupId = group.data.id;

  useEffect(() => {
    fetchGroup(subdomain);
    if (!_.isNil(groupId)) {
      setActiveGroup(groupId, activeGroupId);
    }
  }, [groupId]);

  if (isRequestFailed) return <NotFound goBack={goBack} />;
  if (!groupId) return null;

  const i18nGroupName = group.groupName.get();
  const i18nOrgName = group.organizationName.get();

  return (
    <div className="page-groupjoininstructions">
      <ScrollAwareContext>
        <ContentFrame>
          <ScrollAwareScroller>
            <IntlGroupJoinInstructionsHeader group={group} />

            <div className="page-groupjoininstructions-groupcontent">
              <section>
                <h1 className="__flat-content-padding"><I18nText data={i18nGroupName} /></h1>
                {
                  group.memberCount.enabled.get() && (
                    <h2 className="__flat-content-padding">
                      <FormattedMessage id="joinGroupInstructions.memberCount.message" values={{ string1: <span className="theme-group accent color">{group.memberCount.value.get()}</span> }} />
                    </h2>
                  )
                }

                <I18nText data={group.about.get()}>
                  {
                    (text) => (
                      text
                      ? (
                        <div className="__about __flat-content-padding">
                          <GroupThemeMarkdown text={text} />
                        </div>
                      ) : null
                    )
                  }
                </I18nText>

                <div className="__smallscreen-only">
                  <IntlGroupEligibility
                    groupMetadata={groupMetadata}
                    userData={userData}
                    goToJoinGroup={goToJoinGroup}
                    className="__raised-content-padding"
                  />
                  <div className="__raised-content-padding">
                    <GroupSunset/>
                  </div>
                </div>

                <I18nImageCollection data={group.imagesGallery.get()}>
                  {
                    (images) => {
                      if (images.length === 0) { return null; }
                      const sizes = (images.length % 2 === 1) ? ['100%', '50%', '50%', '50%'] : ['50%', '50%', '50%', '50%'];

                      return (
                        <div className="__gallery">
                          {
                            images.map((g, i) => (
                              <img key={g.id} src={g.url} alt={g.alt_text} width={sizes[i]} style={{ height: 'intrinsic' }} />
                            ))
                          }
                        </div>
                      );
                    }
                  }
                </I18nImageCollection>

                <div className="__research __flat-content-padding">
                  <h2><FormattedMessage id="joinGroupInstructions.groupFocus.heading" /></h2>
                  <div>
                    {
                      group.researchFocused.get()
                      ? <FormattedMessageWithI18nValues
                          phraseId="joinGroupInstructions.groupFocus.research.message"
                          i18nValues={{
                            string1: i18nOrgName,
                            string2: i18nGroupName,
                            string3: i18nOrgName,
                          }}
                        />
                      : <FormattedMessageWithI18nValues
                          phraseId="joinGroupInstructions.groupFocus.default.message"
                          i18nValues={{
                            string1: i18nGroupName,
                          }}
                        />
                    }
                  </div>
                  <div className="__footer">
                    <FormattedMessage id="joinGroupInstructions.sharingData.message" />
                  </div>
                </div>

                <div className="__smallscreen-only">
                  <OrganizationDetails
                    groupMetadata={groupMetadata}
                    className="__flat-content-padding"
                  />
                </div>
              </section>

              <aside>
                <IntlGroupEligibility
                  groupMetadata={groupMetadata}
                  userData={userData}
                  goToJoinGroup={goToJoinGroup}
                />

                <GroupSunset/>

                <OrganizationDetails
                  groupMetadata={groupMetadata}
                />
              </aside>
            </div>
            <section className="page-groupjoininstructions-researchcontent">
              <h2>
                <FormattedMessage id="joinGroupInstructions.aboutBackpack.heading" />
              </h2>
              <p className="font-body">
                <FormattedMessage id="joinGroupInstructions.aboutBackpack.message" />
              </p>
              <div className="__landing-icons __smallscreen-only">
                <LandingIcon
                  Icon={IconPrivacy.Medium}
                  phraseText={{ id: 'landing.privacy.url.name' }}
                  phraseUrl={{ id: 'landing.privacy.url' }}
                  className="font-body font-color-gsDarkest"
                />
                <LandingIcon
                  Icon={IconSecurity.Medium}
                  phraseText={{ id: 'landing.security.url.name' }}
                  phraseUrl={{ id: 'landing.security.url' }}
                  className="font-body font-color-gsDarkest"
                />
              </div>
              <div className="__landing-icons __largescreen-only">
                <LandingIcon
                  Icon={IconPrivacy.Large}
                  phraseText={{ id: 'landing.privacy.url.name' }}
                  phraseUrl={{ id: 'landing.privacy.url' }}
                  className="font-title-3 font-color-gsDarkest"
                />
                <LandingIcon
                  Icon={IconSecurity.Large}
                  phraseText={{ id: 'landing.security.url.name' }}
                  phraseUrl={{ id: 'landing.security.url' }}
                  className="font-title-3 font-color-gsDarkest"
                />
              </div>
            </section>
            <div className="page-groupjoininstructions-footer">
              <div>
                <LogoBackpackRegistered colorName="gray" size="default" imageType="png" />

                <PreferredLanguageDropdownList
                  htmlId="pageLanguage"
                  color="middle-gray"
                  showSelectedFullName={true}
                  size="30"
                />
              </div>

              <div style={{ marginTop: 24 }}>
                <div className="font-caption-1 font-color-middle-gray">
                  <LegalDocumentLinks.PrivacyPolicy className="font-color-middle-gray">
                    <FormattedMessage id="joinGroupInstructions.privacyTerms.privacy.URL.name" />
                  </LegalDocumentLinks.PrivacyPolicy>

                  <LegalDocumentLinks.TermsOfService className="font-color-middle-gray">
                    <FormattedMessage id="joinGroupInstructions.privacyTerms.terms.URL.name" />
                  </LegalDocumentLinks.TermsOfService>
                </div>
              </div>
              <div style={{ marginTop: 8 }} />
              <div className="font-caption-1 font-color-middle-gray">
                <FormattedMessage id="groups.copyright.message.text" />
              </div>
            </div>
          </ScrollAwareScroller>
        </ContentFrame>
      </ScrollAwareContext>
    </div>
  );
};

export default GroupJoinInstructions;
